import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Sun, Moon, Sunset } from "lucide-react";
import { format, addMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isSameDay } from "date-fns";
import TermsDialog from "@/components/TermsDialog";
import { toast } from "sonner";

export default function BookingCalendar() {
    const navigate = useNavigate();
    const [cursor, setCursor] = useState(new Date());
    const [days, setDays] = useState({});  // map dateStr -> day data
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [termsOpen, setTermsOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [credits, setCredits] = useState(0);

    const monthStart = startOfMonth(cursor);
    const monthEnd = endOfMonth(cursor);

    const loadAvailability = async (start, end) => {
        const { data } = await api.get(`/bookings/availability`, { params: { start, end } });
        const map = {};
        for (const d of data.days) map[d.date] = d;
        setDays(map);
    };

    useEffect(() => {
        loadAvailability(format(monthStart, "yyyy-MM-dd"), format(monthEnd, "yyyy-MM-dd"));
        api.get("/credits/me").then((r) => setCredits(r.data.balance)).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cursor]);

    const allDays = useMemo(() => {
        const start = startOfMonth(cursor);
        const end = endOfMonth(cursor);
        // pad to start on Monday (1)
        const first = start.getDay() === 0 ? 6 : start.getDay() - 1; // Mon=0..Sun=6
        const padding = Array.from({ length: first }, () => null);
        const range = eachDayOfInterval({ start, end });
        return [...padding, ...range];
    }, [cursor]);

    const today = new Date();
    const maxDate = new Date(today.getTime() + 60 * 24 * 60 * 60 * 1000);

    const slotCost = selectedSlot === "full_day" ? 2 : 1;

    const isSelectableDate = (d) => {
        if (!d) return false;
        if (d < new Date(today.toDateString())) return false;
        if (d > maxDate) return false;
        if (d.getDay() === 0 || d.getDay() === 6) return false;
        const info = days[format(d, "yyyy-MM-dd")];
        if (!info) return false;
        if (info.is_holiday || info.is_blocked) return false;
        if (info.morning_booked && info.afternoon_booked) return false;
        return true;
    };

    const [pendingApprovalDialog, setPendingApprovalDialog] = useState(null);

    const submitBooking = async () => {
        if (!selectedDate || !selectedSlot) return;
        setSubmitting(true);
        try {
            const r = await api.post("/bookings", {
                date: format(selectedDate, "yyyy-MM-dd"),
                slot: selectedSlot,
                terms_accepted: true,
            });
            setTermsOpen(false);
            setSelectedSlot(null);
            if (r.data?.status === "pending_admin_approval") {
                setPendingApprovalDialog({
                    date: format(selectedDate, "EEE, MMM d, yyyy"),
                    slot: prettySlot(selectedSlot),
                });
            } else {
                toast.success("Booking confirmed", { description: `${format(selectedDate, "EEE, MMM d")} · ${prettySlot(selectedSlot)}` });
            }
            await loadAvailability(format(monthStart, "yyyy-MM-dd"), format(monthEnd, "yyyy-MM-dd"));
            const c = await api.get("/credits/me");
            setCredits(c.data.balance);
        } catch (e) {
            toast.error("Booking failed", { description: formatApiError(e.response?.data?.detail) || e.message });
        } finally {
            setSubmitting(false);
        }
    };

    const selectedInfo = selectedDate ? days[format(selectedDate, "yyyy-MM-dd")] : null;

    return (
        <div className="px-6 md:px-12 lg:px-16 py-10 max-w-7xl mx-auto">
            <p className="text-[11px] uppercase tracking-[0.2em] font-mono text-slate-500 mb-2">Calendar</p>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-bold text-slate-900">Book a pod</h1>
                    <p className="text-base text-slate-600 mt-2">Mon–Fri only · Up to 60 days in advance · Privacy: only Available / Booked is shown.</p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="font-mono text-xs uppercase tracking-[0.14em] text-slate-500">Balance</div>
                    <div className="font-heading text-2xl font-bold text-slate-900" data-testid="calendar-balance">{credits} <span className="text-sm font-normal text-slate-500">credits</span></div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
                <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md p-5 sm:p-7">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="font-heading text-2xl font-bold text-slate-900">{format(cursor, "MMMM yyyy")}</h2>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="icon" onClick={() => setCursor(addMonths(cursor, -1))} data-testid="cal-prev-btn" className="rounded-md h-9 w-9">
                                <ChevronLeft className="h-4 w-4" strokeWidth={1.75} />
                            </Button>
                            <Button variant="outline" size="icon" onClick={() => setCursor(addMonths(cursor, 1))} data-testid="cal-next-btn" className="rounded-md h-9 w-9">
                                <ChevronRight className="h-4 w-4" strokeWidth={1.75} />
                            </Button>
                        </div>
                    </div>
                    <div className="grid grid-cols-5 gap-2 sm:gap-3 mb-3">
                        {["Mon","Tue","Wed","Thu","Fri"].map((d) => (
                            <div key={d} className="text-[10px] uppercase tracking-[0.2em] font-mono text-slate-500 text-center">{d}</div>
                        ))}
                    </div>
                    <div className="grid grid-cols-5 gap-2 sm:gap-3">
                        {allDays.map((d, idx) => {
                            if (!d) return <div key={`p-${idx}`} />;
                            // hide weekends
                            if (d.getDay() === 0 || d.getDay() === 6) return null;
                            const info = days[format(d, "yyyy-MM-dd")];
                            const inMonth = isSameMonth(d, cursor);
                            const past = d < new Date(today.toDateString());
                            const beyond = d > maxDate;
                            const fullyBooked = info && info.morning_booked && info.afternoon_booked;
                            const isHoliday = info?.is_holiday || info?.is_blocked;
                            const selectable = isSelectableDate(d);
                            const isSelected = selectedDate && isSameDay(d, selectedDate);
                            return (
                                <button
                                    key={d.toISOString()}
                                    disabled={!selectable}
                                    onClick={() => { setSelectedDate(d); setSelectedSlot(null); }}
                                    data-testid={`cal-day-${format(d, "yyyy-MM-dd")}`}
                                    className={`group relative aspect-[4/5] sm:aspect-[5/6] rounded-md border text-left p-2.5 transition-all duration-200 ${
                                        isSelected
                                            ? "bg-brand text-white border-brand shadow-sm"
                                            : selectable
                                                ? "bg-white border-slate-200 hover:border-brand hover:shadow-sm"
                                                : "bg-slate-50 border-slate-100 text-slate-300 cursor-not-allowed"
                                    } ${!inMonth ? "opacity-40" : ""}`}
                                >
                                    <div className={`text-xs font-mono ${isSelected ? "text-white/70" : "text-slate-400"}`}>
                                        {format(d, "EEE")}
                                    </div>
                                    <div className={`font-heading text-2xl font-bold mt-0.5 ${isToday(d) && !isSelected ? "text-brand" : ""}`}>
                                        {format(d, "d")}
                                    </div>
                                    <div className="absolute bottom-2 left-2 right-2 flex items-center gap-1">
                                        {past || beyond ? (
                                            <span className={`text-[9px] uppercase tracking-[0.14em] font-mono ${isSelected ? "text-white/70" : "text-slate-300"}`}>—</span>
                                        ) : isHoliday ? (
                                            <span className={`text-[9px] uppercase tracking-[0.14em] font-mono ${isSelected ? "text-white/80" : "text-amber-600"}`}>Closed</span>
                                        ) : fullyBooked ? (
                                            <span className={`text-[9px] uppercase tracking-[0.14em] font-mono ${isSelected ? "text-white/80" : "text-slate-400"}`}>Booked</span>
                                        ) : info ? (
                                            <div className="flex items-center gap-1">
                                                <span className={`h-1.5 w-1.5 rounded-full ${isSelected ? "bg-white" : info.morning_available ? "bg-brand" : "bg-slate-300"}`} />
                                                <span className={`h-1.5 w-1.5 rounded-full ${isSelected ? "bg-white" : info.afternoon_available ? "bg-brand" : "bg-slate-300"}`} />
                                            </div>
                                        ) : null}
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    <div className="flex items-center gap-5 mt-6 pt-5 border-t border-slate-100">
                        <Legend dot="bg-brand" label="Available" />
                        <Legend dot="bg-slate-300" label="Booked" />
                        <Legend dot="bg-amber-500" label="Holiday/Closed" />
                    </div>
                </div>

                {/* Slot selector */}
                <div className="bg-white border border-slate-200 rounded-md p-6 md:p-8 h-fit sticky top-24">
                    <p className="text-[11px] uppercase tracking-[0.2em] font-mono text-slate-500 mb-3">Selected date</p>
                    {!selectedDate ? (
                        <div className="text-sm text-slate-500 py-8 text-center">Choose a weekday on the calendar to see available slots.</div>
                    ) : (
                        <>
                            <h3 className="font-heading text-2xl font-bold text-slate-900">{format(selectedDate, "EEEE, MMM d")}</h3>
                            {selectedInfo?.is_holiday && <p className="text-sm text-amber-600 mt-2">Public holiday: {selectedInfo.holiday_name}</p>}
                            <div className="grid grid-cols-1 gap-3 mt-5">
                                <SlotButton
                                    icon={Sun}
                                    label="Morning"
                                    sub="8:00 AM – 12:00 PM"
                                    cost={1}
                                    disabled={!selectedInfo?.morning_available}
                                    past={selectedInfo?.morning_past_cutoff}
                                    selected={selectedSlot === "morning"}
                                    onClick={() => setSelectedSlot("morning")}
                                    testid="slot-morning"
                                />
                                <SlotButton
                                    icon={Sunset}
                                    label="Afternoon"
                                    sub="1:00 PM – 5:00 PM"
                                    cost={1}
                                    disabled={!selectedInfo?.afternoon_available}
                                    past={selectedInfo?.afternoon_past_cutoff}
                                    selected={selectedSlot === "afternoon"}
                                    onClick={() => setSelectedSlot("afternoon")}
                                    testid="slot-afternoon"
                                />
                                <SlotButton
                                    icon={Moon}
                                    label="Full Day"
                                    sub="8:00 AM – 5:00 PM"
                                    cost={2}
                                    disabled={!(selectedInfo?.morning_available && selectedInfo?.afternoon_available)}
                                    past={selectedInfo?.morning_past_cutoff}
                                    selected={selectedSlot === "full_day"}
                                    onClick={() => setSelectedSlot("full_day")}
                                    testid="slot-full-day"
                                />
                            </div>
                            <div className="mt-6 pt-5 border-t border-slate-100 flex items-center justify-between">
                                <div>
                                    <div className="text-[11px] uppercase tracking-[0.2em] font-mono text-slate-500">Cost</div>
                                    <div className="font-heading text-2xl font-bold text-slate-900">{selectedSlot ? slotCost : 0} credit{selectedSlot && slotCost !== 1 ? "s" : ""}</div>
                                </div>
                                <Button
                                    disabled={!selectedSlot}
                                    onClick={() => {
                                        if (credits < slotCost) {
                                            navigate("/credits");
                                        } else {
                                            setTermsOpen(true);
                                        }
                                    }}
                                    data-testid="cal-confirm-btn"
                                    className="bg-brand hover:bg-brand-hover text-white rounded-md h-11 px-5"
                                >
                                    {credits < slotCost ? "Get more credits" : "Continue"}
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <TermsDialog
                open={termsOpen}
                onOpenChange={setTermsOpen}
                onAccept={submitBooking}
                loading={submitting}
            />

            <Dialog open={!!pendingApprovalDialog} onOpenChange={(o) => !o && setPendingApprovalDialog(null)}>
                <DialogContent data-testid="weekly-cap-dialog" className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl text-slate-900">Weekly maximum reached</DialogTitle>
                        <DialogDescription className="text-slate-600 pt-2 leading-relaxed">
                            You've already booked your 2 full-day weekly maximum. Your request for{" "}
                            <b className="text-slate-900">{pendingApprovalDialog?.date}</b> · <b className="text-slate-900">{pendingApprovalDialog?.slot}</b>{" "}
                            has been submitted to an admin for approval.
                            <br /><br />
                            While we review, the slot remains available to other members. You'll be notified by email and in-app
                            as soon as we decide. <b className="text-slate-900">No credits have been deducted yet.</b>
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            onClick={() => setPendingApprovalDialog(null)}
                            data-testid="weekly-cap-dialog-close"
                            className="bg-brand hover:bg-brand-hover text-white rounded-md w-full"
                        >
                            Got it
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function prettySlot(s) {
    if (s === "morning") return "Morning · 8a–12p";
    if (s === "afternoon") return "Afternoon · 1p–5p";
    return "Full Day";
}

function Legend({ dot, label }) {
    return (
        <div className="flex items-center gap-1.5">
            <span className={`h-2 w-2 rounded-full ${dot}`} />
            <span className="text-xs text-slate-600">{label}</span>
        </div>
    );
}

function SlotButton({ icon: Icon, label, sub, cost, disabled, past, selected, onClick, testid }) {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            data-testid={testid}
            className={`flex items-center gap-3 p-4 border rounded-md text-left transition-all duration-200 ${
                selected
                    ? "border-brand bg-brand/5 ring-1 ring-brand"
                    : disabled
                        ? "border-slate-200 bg-slate-50 text-slate-300 cursor-not-allowed"
                        : "border-slate-200 hover:border-slate-300"
            }`}
        >
            <Icon className={`h-5 w-5 shrink-0 ${selected ? "text-brand" : disabled ? "text-slate-300" : "text-slate-700"}`} strokeWidth={1.75} />
            <div className="flex-1">
                <div className={`font-heading font-semibold ${disabled && !selected ? "text-slate-300" : "text-slate-900"}`}>{label}</div>
                <div className={`text-xs font-mono ${disabled && !selected ? "text-slate-300" : "text-slate-500"}`}>{sub}</div>
            </div>
            {past ? (
                <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-amber-600 bg-amber-50 border border-amber-200 rounded-sm px-2 py-0.5">
                    Started
                </div>
            ) : (
                <div className={`text-xs font-mono ${selected ? "text-brand" : disabled ? "text-slate-300" : "text-slate-500"}`}>
                    {cost} cr
                </div>
            )}
        </button>
    );
}
